/**
 * Takedown API service
 * Handles submitting DMCA takedown requests to the server.
 *
 * These are unauthenticated (public) endpoints, so they go through the shared
 * publicAxios instance per the project convention (publicAxios for public
 * calls, the authenticated axios hook for everything else). Centralizing on
 * one instance gives us a single place for base URL, timeout, and any future
 * interceptors instead of hand-rolled fetch() calls.
 */

import publicAxios from '@/shared/services/publicAxios';

// Public takedown endpoints are mounted under /api on the API server.
const TAKEDOWN_PATH = '/api/takedown';

/**
 * Submit a takedown request to the server
 * @param {Object} takedownData - The takedown form data
 * @param {string} takedownData.requesterName - Name of the requester
 * @param {string} takedownData.requesterEmail - Email of the requester
 * @param {string} takedownData.requesterAddress - Address of the requester
 * @param {string} takedownData.requesterRole - 'owner' or 'agent'
 * @param {string} [takedownData.copyrightOwnerName] - Name of copyright owner (if agent)
 * @param {string} takedownData.contentUrl - URL of infringing content
 * @param {string} takedownData.contentDescription - Description of infringing content
 * @param {string} takedownData.originalWorkDescription - Description of original work
 * @param {string} [takedownData.originalWorkUrl] - URL of original work
 * @param {boolean} takedownData.goodFaithStatement - Good faith statement checkbox
 * @param {boolean} takedownData.accuracyStatement - Accuracy statement checkbox
 * @param {string} takedownData.electronicSignature - Electronic signature
 * @param {File} [takedownData.evidence] - Optional evidence file
 * @param {string} captchaToken - reCAPTCHA token
 * @returns {Promise<Object>} - The response from the server
 */
export async function submitTakedown(takedownData, captchaToken) {
    const formData = new FormData();

    // Add all text fields to FormData
    formData.append('requesterName', takedownData.requesterName);
    formData.append('requesterEmail', takedownData.requesterEmail);
    formData.append('requesterAddress', takedownData.requesterAddress);
    formData.append('requesterRole', takedownData.requesterRole);

    if (takedownData.copyrightOwnerName) {
        formData.append('copyrightOwnerName', takedownData.copyrightOwnerName);
    }

    formData.append('contentUrl', takedownData.contentUrl);
    formData.append('contentDescription', takedownData.contentDescription);
    formData.append(
        'originalWorkDescription',
        takedownData.originalWorkDescription
    );

    if (takedownData.originalWorkUrl) {
        formData.append('originalWorkUrl', takedownData.originalWorkUrl);
    }

    formData.append('goodFaithStatement', takedownData.goodFaithStatement);
    formData.append('accuracyStatement', takedownData.accuracyStatement);
    formData.append('electronicSignature', takedownData.electronicSignature);

    // Add captcha token
    if (captchaToken) {
        formData.append('captchaToken', captchaToken);
    }

    // Add evidence file if provided
    if (takedownData.evidence) {
        formData.append('evidence', takedownData.evidence);
    }

    try {
        // publicAxios defaults to Content-Type: application/json. For a
        // multipart upload we must clear that header so the browser sets
        // multipart/form-data WITH the boundary parameter itself; a hardcoded
        // multipart header with no boundary breaks server-side parsing.
        const response = await publicAxios.post(TAKEDOWN_PATH, formData, {
            headers: { 'Content-Type': undefined },
        });
        return response.data;
    } catch (err) {
        throw new Error(extractErrorMessage(err));
    }
}

/**
 * Get the status of a takedown request
 * @param {string} requestId - The request ID to check
 * @returns {Promise<Object>} - The status information
 */
export async function getTakedownStatus(requestId) {
    try {
        const response = await publicAxios.get(
            `${TAKEDOWN_PATH}/status/${requestId}`
        );
        return response.data;
    } catch (err) {
        throw new Error(extractErrorMessage(err));
    }
}

/**
 * Normalize an axios error into a user-facing message. Preserves the prior
 * fetch behavior: prefer the server's { error } body, then fall back to an
 * HTTP-status message so callers (TakedownForm) keep throwing Error instances
 * with a readable .message.
 * @param {Error} err - The axios error.
 * @returns {string} A human-readable error message.
 */
function extractErrorMessage(err) {
    const serverError = err?.response?.data?.error;
    if (serverError) return serverError;
    if (err?.response?.status) {
        return `HTTP error! status: ${err.response.status}`;
    }
    return err?.message || 'Request failed';
}
